import { of as observableOf, Observable, of, merge } from 'rxjs';

import { map, flatMap, zip } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DataService, ApiService, InspectionPluginService, LanguageService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import { FilesPluginFactoryService } from '../files/files-plugin-factory.service';

@Injectable()
export class FilesAndSuggestionsPluginService extends InspectionPluginService {
  private editedImagesIds = [];
  private comments: any = {};

  public toTypeKeys: any = [
    { value: 1, label: 'plugins.add-photos.technical-service' },
    { value: 2, label: 'plugins.add-photos.customer' }
  ];
  public toTypes: any = [];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private language: LanguageService,
    private filesFactory: FilesPluginFactoryService
  ) {
    super(apiService, dataService, null, filesFactory);
    this.applyTranslations();
    this.language.onLanguageChange().subscribe((value) => this.applyTranslations());
  }

  public applyTranslations() {
    this.toTypes = [];
    this.copyTranslationValues(this.toTypeKeys, this.toTypes, this.language);
  }

  public getToTypes() {
    return this.toTypes;
  }

  public addFiles(inspectionId: string, pointId: string, pluginId: string, files: FormData, photos): Observable<any> {
    return this._addFiles(inspectionId, pointId, pluginId, files, photos).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.setChangedPoint(pointId);
        return this.dataService.updatePlugin(inspectionId, plugin);
      })
    );
  }

  public editFile(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    points: any
  ): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/filesandsuggestions/assets/${assetId}`;
    return this.apiService.post(url, points).pipe(
      map((res) => {
        this.addEditedImage(assetId);
        return res;
      })
    );
  }

  public deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this._deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.deleteFile(inspectionId, res);
      })
    );
  }

  public editComment(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    data: any,
    photo
  ): Observable<any> {
    return this._editComment(inspectionId, pointId, pluginId, assetId, data, photo).pipe(
      map((res: InspectionPlugin) => {
        if (photo) {
          this.addEditedImage(assetId);
        }
        this.dataService.setChangedPoint(pointId);
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public editSuggestion(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    suggestionAssetId: string
  ): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/filesandsuggestions/${assetId}/edit/suggestion`;
    return this.apiService.post(url, { SuggestedAssetId: suggestionAssetId }).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public getEditedImagesIds() {
    return observableOf(this.editedImagesIds);
  }

  private addEditedImage(id) {
    if (!this.editedImagesIds.some((i) => i === id)) {
      this.editedImagesIds.push(id);
    }
  }

  public addLocalComment(assetId: string, comment: string) {
    this.comments[assetId] = comment;
  }

  public getLocalComment(assetId: string) {
    return this.comments[assetId] ? this.comments[assetId] : '';
  }

  public deleteLocalComment(assetId: string) {
    this.comments[assetId] = '';
  }

  public deleteFilesPlugin(inspectionId: string, pointId: string, pluginId: string, assets: string[]): any {
    return this.filesFactory.deleteFiles(inspectionId, pointId, pluginId, assets).pipe(
      flatMap((res) => {
        return super.deletePlugin(inspectionId, pointId, pluginId);
      }),
      map((success) => success)
    );
  }

  private _addFiles(inspectionId: string, pointId: string, pluginId: string, files: FormData, photos): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/filesandsuggestions/${pluginId}`;
    return this.filesFactory.addFiles(inspectionId, pointId, pluginId, photos).pipe(
      flatMap((data) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(data.plugin);
        }
        data.assetValues.forEach((assetValue) => files.append(assetValue.key, assetValue.assetId));
        return this.apiService.postFormData(url, files).pipe(
          zip(this.apiService.post(data.plugin.postServerUrl, data.plugin.config)),
          map(([res, plugin]) => plugin)
        );
      })
    );
  }

  private _deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this.filesFactory.deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        } else {
          return this.apiService.post(plugin.postServerUrl, plugin.config);
        }
      })
    );
  }

  private _editComment(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    data: any,
    photo
  ): Observable<any> {
    const inputFile = new FormData();
    inputFile.append('images', photo);
    inputFile.append('imageAssets[0]', assetId);

    const postFormUrl = `/inspections/${inspectionId}/points/${pointId}/filesandsuggestions/${pluginId}`;
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/filesandsuggestions/${assetId}/edit/comment`;

    return (photo ? this.filesFactory.editFile(inspectionId, pointId, pluginId, assetId, photo) : of(null)).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return photo ? this.apiService.postFormData(postFormUrl, inputFile) : of(null);
      }),
      flatMap((plugin) => {
        return this.filesFactory.editComment(inspectionId, pointId, pluginId, assetId, data);
      }),
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(url, data);
      })
    );
  }
}
